'use client';

import Link from 'next/link';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { sendResetOtp, resetPasswordWithOtp } from '@/lib/api/auth';
import { AuthShell } from '@/components/auth/AuthShell';
import { Button, Field, Input, PasswordInput } from '@/components/ui';

const ErrorIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

export default function ForgotPasswordPage() {
  const router = useRouter();
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email wajib diisi');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await sendResetOtp(email.trim());
      toast.success(r.message || 'Kode dikirim jika email terdaftar');
      setStep('reset');
    } catch (err: any) {
      setError(err?.message || 'Gagal mengirim kode');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      const r = await sendResetOtp(email.trim());
      toast.success(r.message || 'Kode dikirim ulang');
    } catch (err: any) {
      toast.error(err?.message || 'Gagal mengirim ulang kode');
    } finally {
      setResending(false);
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!otp.trim()) {
      setError('Kode OTP wajib diisi');
      return;
    }
    if (password.length < 8) {
      setError('Kata sandi minimal 8 karakter');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Konfirmasi sandi tidak cocok');
      return;
    }
    setLoading(true);
    try {
      const r = await resetPasswordWithOtp({
        email: email.trim(),
        otp: otp.trim(),
        password,
        password_confirmation: passwordConfirmation,
      });
      toast.success(r.message || 'Kata sandi berhasil direset');
      router.push('/login');
    } catch (err: any) {
      setError(err?.message || 'Gagal reset kata sandi');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      headline="Lupa kata sandi? Tenang, kami bantu pulihkan."
      sub="Verifikasi lewat kode OTP yang dikirim ke email instansi Anda, lalu buat sandi baru."
      stats={[
        { num: '6 digit', label: 'Kode OTP' },
        { num: '10 mnt', label: 'Masa berlaku' },
        { num: 'AES-256', label: 'Enkripsi' },
      ]}
    >
      <h1 className="mx-h1" style={{ fontSize: 28, marginBottom: 'var(--mx-sp-2)' }}>Lupa sandi</h1>

      {step === 'email' ? (
        <>
          <p className="mx-body" style={{ marginBottom: 26 }}>
            Masukkan email akun Anda. Kami akan mengirim kode verifikasi 6 digit.
          </p>
          <form onSubmit={handleSendOtp} noValidate>
            <Field label="Email" htmlFor="email" error={error ? <>{ErrorIcon}{error}</> : undefined}>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="nama@instansi.go.id"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                invalid={!!error}
                required
              />
            </Field>
            <div style={{ height: 'var(--mx-sp-4)' }} />
            <Button type="submit" variant="primary" size="lg" block loading={loading}>Kirim kode</Button>
          </form>
        </>
      ) : (
        <>
          <p className="mx-body" style={{ marginBottom: 26 }}>
            Jika <strong>{email}</strong> terdaftar, kodenya sudah dikirim. Masukkan kode dan sandi baru Anda.
          </p>
          <form onSubmit={handleReset} noValidate>
            <Field label="Kode OTP" htmlFor="otp" hint="6 digit, berlaku 10 menit">
              <Input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                required
              />
            </Field>

            <div style={{ height: 'var(--mx-sp-4)' }} />

            <Field label="Kata sandi baru" htmlFor="pw">
              <PasswordInput
                id="pw"
                autoComplete="new-password"
                placeholder="Minimal 8 karakter"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>

            <div style={{ height: 'var(--mx-sp-4)' }} />

            <Field label="Konfirmasi sandi baru" htmlFor="pw2" error={error ? <>{ErrorIcon}{error}</> : undefined}>
              <PasswordInput
                id="pw2"
                autoComplete="new-password"
                placeholder="Ulangi sandi baru"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                invalid={!!error}
                required
              />
            </Field>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '14px 0 22px' }}>
              <button
                type="button"
                className="mx-btn mx-btn--ghost mx-btn--sm"
                onClick={() => { setStep('email'); setError(''); }}
              >
                ← Ubah email
              </button>
              <button
                type="button"
                className="mx-btn mx-btn--ghost mx-btn--sm"
                style={{ color: 'var(--mx-accent)' }}
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? 'Mengirim…' : 'Kirim ulang kode'}
              </button>
            </div>

            <Button type="submit" variant="primary" size="lg" block loading={loading}>Reset sandi</Button>
          </form>
        </>
      )}

      <p className="mx-caption" style={{ textAlign: 'center', marginTop: 22 }}>
        Ingat sandi Anda?{' '}
        <Link href="/login" style={{ color: 'var(--mx-accent)', fontWeight: 600, textDecoration: 'none' }}>Masuk</Link>
      </p>
    </AuthShell>
  );
}
